<template>
  <Teleport to="body">
    <Transition name="slide-up">
      <DraggableWrapper
        v-if="metronomeStore.isOpen && !metronomeStore.isFullscreen"
        ref="wrapperRef"
        :x="pos.x"
        :y="pos.y"
        :handleOnly="true"
        positionType="fixed"
        :constrainToViewport="true"
        :dragZIndex="61"
        @update:position="savePos"
        class="z-[60] max-w-[calc(100vw-2rem)]"
      >
      <div
        class="relative flex items-center gap-2.5 rounded-2xl pl-2.5 pr-2.5 py-3 shadow-2xl select-none text-white transition-shadow"
        style="background-color: #1c2547"
        :class="isRunning ? 'ring-2 ring-emerald-400 shadow-emerald-500/20' : ''"
        role="status"
        :aria-label="$t('metronome.minimizedLabel')"
      >
        <!-- Alça de arraste (grip padrão do sistema) -->
        <span
          class="handle cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300 shrink-0 touch-none"
          :title="$t('metronome.dragHandle')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h.008v.008H8.25V6.75zm0 3h.008v.008H8.25V9.75zm0 3h.008v.008H8.25v-.008zm0 3h.008v.008H8.25v-.008zm3-9h.008v.008H11.25V6.75zm0 3h.008v.008H11.25V9.75zm0 3h.008v.008H11.25v-.008zm0 3h.008v.008H11.25v-.008zm3-9h.008v.008H14.25V6.75zm0 3h.008v.008H14.25V9.75zm0 3h.008v.008H14.25v-.008zm0 3h.008v.008H14.25v-.008z" />
          </svg>
        </span>
        <!-- Play / Stop -->
        <button
          type="button"
          @click="$emit('toggle-play')"
          :class="[
            'p-2.5 rounded-full transition-colors flex items-center justify-center shadow-sm text-white',
            isRunning
              ? 'bg-rose-500 hover:bg-rose-600 animate-pulse'
              : 'bg-emerald-600 hover:bg-emerald-500'
          ]"
          :title="isRunning ? $t('metronome.stop') : $t('metronome.start')"
        >
          <svg v-if="isRunning" class="w-4 h-4 fill-current" viewBox="0 0 24 24">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
          <svg v-else class="w-4 h-4 fill-current" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        </button>

        <!-- BPM + beat dots -->
        <button
          type="button"
          @click="$emit('expand')"
          :title="$t('metronome.expandTitle')"
          class="flex flex-col items-center hover:opacity-90 transition-opacity min-w-[64px]"
        >
          <span class="text-2xl font-black font-mono leading-none tracking-tight">
            {{ bpm }}
          </span>
          <span class="text-[9px] font-bold uppercase tracking-widest text-slate-400">
            {{ $t('metronome.bpm') }}
          </span>
          <span class="flex items-center gap-1 mt-1.5">
            <span
              v-for="b in totalBeats"
              :key="b"
              class="w-1.5 h-1.5 rounded-full transition-all duration-75"
              :class="[
                currentBeat === b - 1 && isRunning
                  ? b === 1
                    ? 'bg-amber-400 scale-125'
                    : 'bg-emerald-400 scale-110'
                  : 'bg-slate-600'
              ]"
            ></span>
          </span>
        </button>

        <!-- Ações: expandir + fechar -->
        <div class="flex flex-col items-center gap-0.5">
          <button
            type="button"
            @click="$emit('expand')"
            class="p-1.5 text-slate-300 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
            :title="$t('metronome.expandTitle')"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
            </svg>
          </button>
          <button
            type="button"
            @click="$emit('close')"
            class="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors"
            :title="$t('metronome.closeTitle')"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      </DraggableWrapper>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed, watch, defineProps, defineEmits } from 'vue';
import metronomeStore from '@/store/metronome.store';
import tunerStore from '@/store/tuner.store';
import DraggableWrapper from '@/components/common/elements/DraggableWrapper.vue';
import { useFloatingMini } from '@/composables/useFloatingMini';

defineProps({
  bpm: { type: Number, default: 120 },
  isRunning: { type: Boolean, default: false },
  currentBeat: { type: Number, default: 0 },
  totalBeats: { type: Number, default: 4 },
});

defineEmits(['toggle-play', 'expand', 'close']);

// Empilha acima da mini do afinador quando ela está visível
const tunerBarVisible = computed(() => tunerStore.isOpen && !tunerStore.isFullscreen);

const { pos, wrapperRef, savePos, recomputeDefault } = useFloatingMini('mini-metronome-position', {
  estimate: { w: 230, h: 100 },
  getBottomGap: () => (tunerBarVisible.value ? 132 : 0),
});

watch(tunerBarVisible, () => recomputeDefault());
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.25s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
